import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useMemo, useState } from "react";
import Input, { InputType } from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateEnergyMutation, useGetEnvironnementsQuery, useUpdateEnergyMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableEnergyRow = {
  id?: number | string;
  code?: string;
  label?: string;
  price?: number | string;
  unit?: string;
  unitco2?: string;
  co2?: number | string;
  envs?: unknown;
  countries?: string;
};

type EnvironmentOption = {
  value: string;
  code: string;
  label: string;
};

function toCheckedEnvMap(row: EditableEnergyRow | null | undefined, environments: EnvironmentOption[]) {
  const selected = row?.envs;
  let values: string[] = [];

  if (Array.isArray(selected)) {
    values = selected
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const env = item as Record<string, unknown>;
          const raw = env.value ?? env.id ?? env.code;
          return typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
        }
        return String(item);
      })
      .map((item) => item.trim())
      .filter(Boolean);
  } else if (typeof selected === "string") {
    values = selected.split(",").map((item) => item.trim()).filter(Boolean);
  }

  return environments.reduce<Record<string, boolean>>((acc, env) => {
    acc[env.code] = values.includes(env.value) || values.includes(env.code);
    return acc;
  }, {});
}

function getCheckedEnvIds(stateEnvs: Record<string, boolean>, environments: EnvironmentOption[]) {
  return environments.filter((env) => stateEnvs[env.code]).map((env) => env.value);
}

type EnergyPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  isSuperContext?: boolean;
  envNumeric: number | undefined;
  lang: string;
  row?: EditableEnergyRow | null;
  onSaved?: () => Promise<void> | void;
};

const EnergyPopup: React.FC<EnergyPopupProps> = ({
  isOpen,
  onClose,
  request,
  isSuperContext = false,
  envNumeric,
  lang,
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [envChecks, setEnvChecks] = useState<Record<string, boolean>>({});
  const { data: rawEnvironments = [] } = useGetEnvironnementsQuery("");
  const environments = useMemo<EnvironmentOption[]>(() => {
    return (Array.isArray(rawEnvironments) ? rawEnvironments : []).map((env: any) => ({
      value: String(env.value ?? env.id ?? ""),
      code: String(env.code ?? ""),
      label: String(env.label ?? env.country ?? env.text ?? env.code ?? env.value ?? env.id ?? ""),
    }));
  }, [rawEnvironments]);
  const isCreateMode = row == null;
  const isSuperMode =
    isSuperContext ||
    /energy[_\s-]*mgt|energies[_\s-]*mgt/i.test(request?.url ?? "");
  const [updateEnergy] = useUpdateEnergyMutation();
  const [createEnergy] = useCreateEnergyMutation();

  const { control, reset, getValues } = useCustomForm<{
    code: string;
    label: string;
    price: string;
    unit: string;
    unitco2: string;
    co2: string;
  }>({
    defaultValues: {
      code: "",
      label: "",
      price: "0",
      unit: "",
      unitco2: "",
      co2: "0",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    reset({
      code: String(row?.code ?? ""),
      label: String(row?.label ?? ""),
      price: String(row?.price ?? "0"),
      unit: String(row?.unit ?? ""),
      unitco2: String(row?.unitco2 ?? ""),
      co2: String(row?.co2 ?? "0"),
    });
    setEnvChecks(toCheckedEnvMap(row, environments));
  }, [
    environments,
    isOpen,
    reset,
    row?.code,
    row?.co2,
    row?.envs,
    row?.label,
    row?.price,
    row?.unit,
    row?.unitco2,
  ]);

  const handleSave = async () => {
    if (!request || envNumeric == null) return;
    const currentRow = row;
    const rowId = currentRow?.id ?? (currentRow as any)?.key;
    if (!isCreateMode && (rowId == null || String(rowId).trim() === "")) return;

    const { code, label, price, unit, unitco2, co2 } = getValues();
    const parsedPrice = Number.parseFloat(String(price).replace(",", "."));
    const parsedCo2 = Number.parseFloat(String(co2).replace(",", "."));
    const checkedEnvs = getCheckedEnvIds(envChecks, environments);
    const countries = environments
      .filter((env) => envChecks[env.code])
      .map((env) => env.code)
      .join(", ");

    setIsSaving(true);
    try {
      const baseUrl = request.url
        .replace(/\/(?:##id##|\{id\}|:id|\d+)$/i, "")
        .replace(/[?&](?:id=##id##|id=\{id\}|id=:id)(?=&|$)/i, "")
        .replace(/[?&]$/, "")
        .replace(/\/+$/, "");
      const url = isCreateMode ? baseUrl : `${baseUrl}/${rowId}`;

      const payload: Record<string, unknown> = isSuperMode
        ? {
            id: isCreateMode ? null : rowId,
            code,
            envs: checkedEnvs,
            unit,
            unitco2,
            countries,
            env: envNumeric,
            lang,
          }
        : {
            id: isCreateMode ? null : rowId,
            code: isCreateMode ? code : currentRow?.code,
            label,
            price: Number.isNaN(parsedPrice) ? 0 : parsedPrice,
            unit: isCreateMode ? unit : currentRow?.unit,
            unitco2: isCreateMode ? unitco2 : currentRow?.unitco2,
            co2: Number.isNaN(parsedCo2) ? 0 : parsedCo2,
            envs: checkedEnvs,
            env: envNumeric,
            lang,
          };

      if (isCreateMode) {
        await createEnergy({
          url,
          method: "POST",
          data: payload,
        }).unwrap();
      } else {
        await updateEnergy({
          url,
          method: "PUT",
          data: payload,
        }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title="Modifier énergie"
      onClose={onClose}
      portal
      fixSize={isSuperMode ? "large" : "middle"}
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(row ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            {/* <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Code"}
                  name="code"
                  control={control}
                  disabled={!isSuperMode}
                  size="large"
                />
              </group>
            </group> */}

            {!isSuperMode && (
              <>
                <group data-gap="20">
                  <group data-length="forcefit" data-fit="1">
                    <Input
                      dataLength="autofit"
                      label={"Code"}
                      name="code"
                      control={control}
                      disabled={!isSuperMode}
                      size="large"
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="forcefit" data-fit="1">
                    <Input
                      dataLength="autofit"
                      label={"Label"}
                      name="label"
                      control={control}
                      type="text"
                      disabled={isSuperMode}
                      size="large"
                    />
                  </group>
                </group>

                <group data-gap="20">
                  <group data-length="forcefit" data-fit="1">
                    <Input
                      dataLength="autofit"
                      label={"Price"}
                      name="price"
                      control={control}
                      type="number"
                      disabled={isSuperMode}
                      size="large"
                      step={0.01}
                      min={0}
                    />
                  </group>
                  <group data-length="forcefit" data-fit="1">
                    <Input
                      dataLength="autofit"
                      label={"CO2"}
                      name="co2"
                      control={control}
                      type="number"
                      disabled={isSuperMode}
                      size="large"
                      step={0.01}
                      min={0}
                    />
                  </group>
                </group>
              </>
            )}

            <group data-gap="20">
              {isSuperMode && <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Code"}
                  name="code"
                  control={control}
                  disabled={!isSuperMode}
                  size="large"
                />
              </group> }
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Unit"}
                  name="unit"
                  control={control}
                  type="text"
                  disabled={!isSuperMode}
                  size="large"
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Unit CO2"}
                  name="unitco2"
                  control={control}
                  type="text"
                  disabled={!isSuperMode}
                  size="large"
                />
              </group>
            </group>

            {isSuperMode && (
              <group data-direction="column" data-gap="8" data-space-top="10">
                <text data-weight="700">{t("environnement_mgt")}</text>
                <group data-gap="20" data-wrap="wrap">
                  {environments.length === 0 && <text>{t("No data")}</text>}
                  {environments.map((env, index) => (
                    <group key={`${env.code}-${env.value}-${index}`} data-width="auto">
                      <Input
                        id={`energy-env-${env.code}-${index}`}
                        type={InputType.Checkbox}
                        name={`env_${env.value}`}
                        label={env.label}
                        checked={Boolean(envChecks[env.code])}
                        onCustomChange={(_name, checked) => {
                          setEnvChecks((prev) => ({ ...prev, [env.code]: Boolean(checked) }));
                        }}
                      />
                    </group>
                  ))}
                </group>
              </group>
            )}
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default EnergyPopup;